import { useQuery, useReactiveVar } from "@apollo/client";

import { authenticationStateVar } from "@/apollo/auth-state";
import { AdminPageShell } from "@/components/admin-page-shell";
import { DraftPreviewPanel } from "@/components/draft-preview-panel";
import { FullPageLoading } from "@/components/full-page-loading";
import { PageIntro } from "@/components/page-intro";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  GET_DRAFT_PREVIEW,
  type GetDraftPreviewData,
  type GetDraftPreviewVariables,
} from "@/graphql";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useRelativeTime } from "@/hooks/use-relative-time";

export function PreviewPage() {
  useDocumentTitle("Preview");
  const { selectedAccount } = useReactiveVar(authenticationStateVar);

  const { data, loading, error } = useQuery<
    GetDraftPreviewData,
    GetDraftPreviewVariables
  >(GET_DRAFT_PREVIEW, {
    variables: { instagramUsername: selectedAccount },
    skip: !selectedAccount,
  });

  const updatedAtValue = data?.draftPreview?.updatedAt;
  const updatedAt = updatedAtValue
    ? /^[0-9]+$/.test(updatedAtValue)
      ? Number.parseInt(updatedAtValue, 10)
      : Date.parse(updatedAtValue)
    : null;
  const { relative, absolute } = useRelativeTime(updatedAt);

  if (!selectedAccount) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center">
        <p className="font-inter text-sm text-neutral-500">
          Select an Instagram account to preview your link.
        </p>
      </div>
    );
  }

  if (loading) {
    return <FullPageLoading label="Loading your draft…" />;
  }

  if (error || !data?.draftPreview) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center">
        <p className="font-inter text-sm text-red-600">
          Couldn&apos;t load your draft. Please refresh and try again.
        </p>
      </div>
    );
  }

  return (
    <AdminPageShell className="max-w-4xl">
      <div className="space-y-8">
        <PageIntro
          title="Preview"
          description="Check how your Sked Link will look on mobile before you publish changes to your live page."
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-2 rounded-full border border-neutral-100 bg-white px-3 py-1.5 font-inter text-xs font-medium text-neutral-600">
                <i className="fa-regular fa-clock" />
                Draft saved {relative}
              </span>
            </TooltipTrigger>
            <TooltipContent>{absolute}</TooltipContent>
          </Tooltip>
        </PageIntro>

        <DraftPreviewPanel draftPreview={data.draftPreview} />
      </div>
    </AdminPageShell>
  );
}
